import {
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import {
  createQuestionQuota,
  createQuestionQuotasBulk,
  deleteQuestionQuota,
  fetchQuestionQuota,
  fetchQuestionQuotas,
  removeQuestionQuotas,
  updateQuestionQuota,
} from 'services/backend/questionQuotas';
import { ReactQueryFunctionCallbacks } from 'types/internal';

export const questionQuotaQueries = {
  all: (opts: { surveyId: number }) => [
    'surveys',
    opts.surveyId,
    'question-quotas',
  ],
  forSurvey: (opts: { surveyId: number; waveIds: number[] }) =>
    queryOptions({
      queryFn: () =>
        fetchQuestionQuotas({
          surveyId: opts.surveyId,
          waveIds: opts.waveIds,
        }),
      queryKey: [
        ...questionQuotaQueries.all({ surveyId: opts.surveyId }),
        opts.waveIds,
      ],
    }),
  get: (opts: { questionQuotaId: number }) =>
    queryOptions({
      queryFn: () =>
        fetchQuestionQuota({ questionQuotaId: opts.questionQuotaId }),
      queryKey: ['question-quotas', opts.questionQuotaId],
    }),
};

export function useRemoveQuestionQuotas(
  opts?: ReactQueryFunctionCallbacks<typeof removeQuestionQuotas>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...opts,
    mutationFn: removeQuestionQuotas,
    onSuccess: (...args) => {
      queryClient.invalidateQueries();

      opts?.onSuccess?.(...args);
    },
  });
}

export function useCreateQuestionQuota(
  opts?: ReactQueryFunctionCallbacks<typeof createQuestionQuota>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...opts,
    mutationFn: createQuestionQuota,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey.includes('question-quotas') ||
          query.queryKey.includes('questions'),
      });

      opts?.onSuccess?.(...args);
    },
  });
}

export function useCreateQuestionQuotasBulk(
  opts?: ReactQueryFunctionCallbacks<typeof createQuestionQuotasBulk>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...opts,
    mutationFn: createQuestionQuotasBulk,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey.includes('question-quotas') ||
          query.queryKey.includes('questions'),
      });

      opts?.onSuccess?.(...args);
    },
  });
}

export function useUpdateQuestionQuota(
  opts?: ReactQueryFunctionCallbacks<typeof updateQuestionQuota>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...opts,
    mutationFn: updateQuestionQuota,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey.includes('question-quotas') ||
          query.queryKey.includes('questions'),
      });

      opts?.onSuccess?.(...args);
    },
  });
}

export function useDeleteQuestionQuota(
  opts?: ReactQueryFunctionCallbacks<typeof deleteQuestionQuota>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...opts,
    mutationFn: deleteQuestionQuota,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey.includes('question-quotas') ||
          query.queryKey.includes('questions'),
      });

      opts?.onSuccess?.(...args);
    },
  });
}
